import { create } from "zustand";

const getRemaining = (ticket) => {
  if (ticket.quantity_total != null && ticket.quantity_sold != null) {
    return Math.max(0, ticket.quantity_total - ticket.quantity_sold);
  }
  return 20;
};

export const useCartStore = create((set, get) => ({
  items: [],
  tables: [], // top-level — { id, name, display_price, eventId, tickets_included_enabled, tickets_included_count }
  isDrawerOpen: false,

  openDrawer: () => set({ isDrawerOpen: true }),
  closeDrawer: () => set({ isDrawerOpen: false }),
  toggleDrawer: () => set((s) => ({ isDrawerOpen: !s.isDrawerOpen })),

  // ── TICKET ACTIONS ──

  addItem: (event, ticket, quantity = 1) => {
    const items = get().items || [];
    const remaining = getRemaining(ticket);
    const existing = items.find(
      (i) => i.ticket.id === ticket.id && i.eventId === event.id,
    );

    if (existing) {
      const newQty = Math.min(existing.quantity + quantity, remaining);
      set({
        items: items.map((i) =>
          i.ticket.id === ticket.id && i.eventId === event.id
            ? { ...i, quantity: newQty }
            : i,
        ),
      });
    } else {
      set({
        items: [
          ...items,
          {
            eventId: event.id,
            eventName: event.name,
            eventFlyer: event.flyer_portrait,
            eventDate: event.date_start,
            currency: event.currency,
            commission: event.client_commission_percentage ?? 0,
            commissionVatIncluded: event.commission_vat_included ?? false,
            commissionVatPercentage: event.commission_vat_percentage ?? 0,
            can_buy_without_login: event.can_buy_without_login ?? true,
            is_tables_live: event.is_tables_live ?? false,
            pixels: event.pixels || [],
            ticket,
            quantity: Math.min(quantity, remaining),
          },
        ],
      });
    }
  },

  removeItem: (ticketId, eventId) => {
    set((s) => ({
      items: (s.items || []).filter(
        (i) => !(i.ticket.id === ticketId && i.eventId === eventId),
      ),
    }));
  },

  updateQuantity: (ticketId, eventId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(ticketId, eventId);
      return;
    }
    set((s) => ({
      items: (s.items || []).map((i) => {
        if (i.ticket.id === ticketId && i.eventId === eventId) {
          const remaining = getRemaining(i.ticket);
          return { ...i, quantity: Math.min(quantity, remaining) };
        }
        return i;
      }),
    }));
  },

  clearCart: () => set({ items: [], tables: [] }),

  // ── TABLE ACTIONS ──

  addTable: (table) => {
    const tables = get().tables || [];
    const existing = tables.find((t) => t.id === table.id);
    if (existing) return;
    set({ tables: [...tables, table] });
  },

  removeTable: (tableId) => {
    set((s) => ({
      tables: (s.tables || []).filter((t) => t.id !== tableId),
    }));
  },

  clearTables: () => set({ tables: [] }),

  // ── GETTERS ──

  getSubtotal: () =>
    (get().items || []).reduce(
      (sum, i) => sum + i.ticket.price * i.quantity,
      0,
    ),

  getCommission: () => {
    const items = get().items || [];
    return items.reduce((sum, i) => {
      const itemSubtotal = i.ticket.price * i.quantity;
      const rawCommission = (itemSubtotal * (i.commission ?? 0)) / 100;
      const vatRate = (i.commissionVatPercentage ?? 0) / 100;
      const vatIncluded = i.commissionVatIncluded ?? false;
      if (vatIncluded) {
        return sum + rawCommission / (1 + vatRate);
      } else {
        return sum + rawCommission;
      }
    }, 0);
  },

  getVat: () => {
    const items = get().items || [];
    return items.reduce((sum, i) => {
      const itemSubtotal = i.ticket.price * i.quantity;
      const rawCommission = (itemSubtotal * (i.commission ?? 0)) / 100;
      const vatRate = (i.commissionVatPercentage ?? 0) / 100;
      const vatIncluded = i.commissionVatIncluded ?? false;
      if (vatRate === 0) return sum;
      if (vatIncluded) {
        return sum + rawCommission - rawCommission / (1 + vatRate);
      } else {
        return sum + rawCommission * vatRate;
      }
    }, 0);
  },

  getTableTotal: () =>
    (get().tables || []).reduce(
      (sum, t) => sum + parseFloat(t.display_price || 0),
      0,
    ),

  getTotal: () => {
    const store = get();
    const items = store.items || [];
    const vatIncluded = items[0]?.commissionVatIncluded ?? false;
    const tableTotal = store.getTableTotal();
    if (vatIncluded) {
      return (
        store.getSubtotal() +
        items.reduce((sum, i) => {
          const itemSubtotal = i.ticket.price * i.quantity;
          return sum + (itemSubtotal * (i.commission ?? 0)) / 100;
        }, 0) +
        tableTotal
      );
    } else {
      return (
        store.getSubtotal() +
        store.getCommission() +
        store.getVat() +
        tableTotal
      );
    }
  },

  getTotalItems: () =>
    (get().items || []).reduce((sum, i) => sum + i.quantity, 0) +
    (get().tables || []).length,
}));
